/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { axiosInstance } from '@/lib/axios'
import {
  AuthUser,
  LoginRequest,
  LoginResponse,
  RegisterResponse,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangeEmailAndPasswordRequest,
  ExchangeTokenRequest,
  ExchangeTokenResponse,
} from '../types/auth'
import BaseService from './baseService'

class AuthService extends BaseService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // const form = new URLSearchParams()

      // form.append('username', credentials.username)
      // form.append('password', credentials.password)
      // form.append('created_type', credentials.created_type)

      // const isProduction = import.meta.env.PROD

      // const loginUrl = isProduction
      //   ? `${import.meta.env.VITE_API_URL}/user/login`
      //   : '/api/user/login'
      const loginUrl = `${import.meta.env.VITE_API_URL}/user/login`

      const { data } = await axios.post<LoginResponse>(loginUrl, credentials, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        // eslint-disable-next-line no-console
        console.error('Login error:', error.response.data)
        return error.response.data
      }
      throw error
    }
  }

  async refreshToken(
    access_token: string,
    refresh_token: string
  ): Promise<any> {
    try {
      // Use fetch/axios directly to avoid BaseService auth header coupling
      const API_URL = import.meta.env.VITE_API_URL
      const res = await fetch(`${API_URL}/user/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${access_token}` },
        body: JSON.stringify({ refresh_token }),
        credentials: 'include',
      })
      const data = await res.json()
      if (!res.ok) throw data
      return data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to refresh token'
        )
      }
      throw error
    }
  }

  async exchangeToken(request: ExchangeTokenRequest): Promise<ExchangeTokenResponse> {
    try {
      const response = await axiosInstance.post(`/user/auth/exchange`, request)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.detail || 'Failed to exchange token'
        )
      }
      throw error
    }
  }

  async getMe() {
    try {
      const response = await axiosInstance.get(`/user/me`)
      const user: AuthUser = response.data
      // // Enforce role on restore to avoid cross-site token with different role
      // if (!user || user.role !== Role.TUTOR) {
      //   console.log('user role mismatch')
      //   authService.logout()
      // }

      return user
    } catch (error) {
      useAuthStore.getState().auth.reset()
      throw error
    }
  }

  async logout() {
    try {
      useAuthStore.getState().auth.reset()
      // await this.post(`/api/v1/auth/logout`)

      return true
    } catch (_error) {
      useAuthStore.getState().auth.reset()
      return true
    }
  }

  async register(request: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await axiosInstance.post(`/user/register-manual`, {
        ...request,
        user_role: request.user_role.toUpperCase(),
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.detail
        )
      }
      throw error
    }
  }

  async requestPasswordReset(request: ForgotPasswordRequest): Promise<void> {
    try {
      const response = await axiosInstance.post(`/user/auth/request-reset`, request)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.detail || 'Failed to request password reset'
        )
      }
      throw error
    }
  }

  async resetPassword(request: ResetPasswordRequest): Promise<void> {
    try {
      const response = await axiosInstance.post(`/user/auth/reset-password`, request)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.detail || 'Failed to reset password'
        )
      }
      throw error
    }
  }

  async changeEmailAndPassword(request: ChangeEmailAndPasswordRequest): Promise<void> {
    try {
      const response = await axiosInstance.post(`/user/change-email-and-password`, request)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.detail || 'Failed to change email and password'
        )
      }
      throw error
    }
  }
}

export const authService = new AuthService()
